export type DoorTypeId = 'draaideur' | 'dubbele-draaideur' | 'taatsdeur' | 'schuifdeur'

export type DivisionId = 'strak' | 'horizontaal' | 'verticaal' | 'raster' | 'herenhuis' | 'bauhaus' | 'artdeco' | 'eigen'

export type GlassId = 'helder' | 'mat' | 'op-aanvraag'

export type FinishId = 'ral9005' | 'ral9016' | 'ral'

export type ProfileId = 'slank' | 'klassiek' | 'robuust'

export type HandleId = 'kruk' | 'klein' | 'lang' | 'vaste-kruk' | 'op-aanvraag'

export type HandleFinishId = 'zwart' | 'rvs' | 'messing' | 'brons'

export type SidePanelOption = 'geen' | 'links' | 'rechts' | 'beide'

export type HingeSide = 'links' | 'rechts'

export interface DivisionBar {
    orientation: 'h' | 'v'
    at: number
    from: number | null
    to: number | null
}

export interface DoorConfig {
    version: 2
    doorType: DoorTypeId
    width: number
    height: number
    division: DivisionId
    rows: number
    cols: number
    bars: DivisionBar[]
    glass: GlassId
    glassNote: string
    finish: FinishId
    ralCode: string
    profile: ProfileId
    handle: HandleId
    handleNote: string
    handleFinish: HandleFinishId
    sidePanels: SidePanelOption
    sidePanelWidth: number
    transom: boolean
    transomHeight: number
    bottomPanel: boolean
    hinge: HingeSide
    lock: boolean
    softClose: boolean
}

export interface Range {
    min: number
    max: number
    default: number
}

export interface DoorTypeOption {
    id: DoorTypeId
    name: string
    tagline: string
    description: string
    width: Range
    height: Range
    leaves: number
    allowsSidePanels: boolean
    allowsTransom: boolean
    handles: HandleId[]
}

export const DOOR_TYPES: DoorTypeOption[] = [
    {
        id: 'draaideur',
        name: 'Draaideur',
        tagline: 'Enkele deur op scharnieren',
        description: "De tijdloze stalen deur die naar één kant opendraait. Ideaal tussen hal en woonkamer.",
        width: { min: 700, max: 1200, default: 930 },
        height: { min: 2000, max: 2800, default: 2315 },
        leaves: 1,
        allowsSidePanels: true,
        allowsTransom: true,
        handles: ['kruk', 'klein', 'lang', 'vaste-kruk', 'op-aanvraag']
    },
    {
        id: 'dubbele-draaideur',
        name: 'Dubbele deur',
        tagline: 'Twee deuren, één royale doorgang',
        description: "Twee symmetrische deurbladen voor brede doorgangen tussen woonkamer en keuken of eetkamer.",
        width: { min: 1200, max: 2200, default: 1600 },
        height: { min: 2000, max: 2800, default: 2315 },
        leaves: 2,
        allowsSidePanels: true,
        allowsTransom: true,
        handles: ['kruk', 'klein', 'lang', 'vaste-kruk', 'op-aanvraag']
    },
    {
        id: 'taatsdeur',
        name: 'Taatsdeur',
        tagline: 'Draait om een verticale as',
        description: "Het architectonische statement. Draait soepel om een taatsscharnier en kan extra breed en hoog worden uitgevoerd.",
        width: { min: 800, max: 1500, default: 1000 },
        height: { min: 2000, max: 3000, default: 2400 },
        leaves: 1,
        allowsSidePanels: true,
        allowsTransom: true,
        handles: ['kruk', 'klein', 'lang', 'vaste-kruk', 'op-aanvraag']
    },
    {
        id: 'schuifdeur',
        name: 'Schuifdeur',
        tagline: 'Glijdt langs de wand',
        description: "Schuift geruisloos langs de muur aan een zichtbare stalen rail. Perfect waar geen ruimte is om te draaien.",
        width: { min: 800, max: 1500, default: 1000 },
        height: { min: 2000, max: 2800, default: 2315 },
        leaves: 1,
        allowsSidePanels: false,
        allowsTransom: false,
        handles: ['klein', 'lang', 'op-aanvraag']
    }
]

export interface DivisionOption {
    id: DivisionId
    name: string
    description: string
    adjustRows: boolean
    adjustCols: boolean
    rows: { min: number, max: number, default: number }
    cols: { min: number, max: number, default: number }
}

export const DIVISIONS: DivisionOption[] = [
    {
        id: 'strak',
        name: 'Strak',
        description: "Eén doorlopend glasvlak voor maximale lichtinval.",
        adjustRows: false,
        adjustCols: false,
        rows: { min: 0, max: 0, default: 0 },
        cols: { min: 0, max: 0, default: 0 }
    },
    {
        id: 'horizontaal',
        name: 'Loft',
        description: "Horizontale roeden, de klassieke industriële look.",
        adjustRows: true,
        adjustCols: false,
        rows: { min: 1, max: 8, default: 3 },
        cols: { min: 0, max: 0, default: 0 }
    },
    {
        id: 'verticaal',
        name: 'Verticaal',
        description: "Slanke verticale lijnen die de hoogte benadrukken.",
        adjustRows: false,
        adjustCols: true,
        rows: { min: 0, max: 0, default: 0 },
        cols: { min: 1, max: 3, default: 1 }
    },
    {
        id: 'raster',
        name: 'Atelier',
        description: "Raster van horizontale en verticale roeden, zoals in een oud atelier.",
        adjustRows: true,
        adjustCols: true,
        rows: { min: 1, max: 8, default: 4 },
        cols: { min: 1, max: 3, default: 1 }
    },
    {
        id: 'herenhuis',
        name: 'Herenhuis',
        description: "Klassieke indeling met drie kleine vakken bovenin.",
        adjustRows: false,
        adjustCols: false,
        rows: { min: 0, max: 0, default: 0 },
        cols: { min: 0, max: 0, default: 0 }
    },
    {
        id: 'bauhaus',
        name: 'Bauhaus',
        description: "Ladders aan beide zijden rond een open middenvlak.",
        adjustRows: false,
        adjustCols: false,
        rows: { min: 0, max: 0, default: 0 },
        cols: { min: 0, max: 0, default: 0 }
    },
    {
        id: 'artdeco',
        name: 'Art deco',
        description: "Grafische vakverdeling boven en onder, rust in het midden.",
        adjustRows: false,
        adjustCols: false,
        rows: { min: 0, max: 0, default: 0 },
        cols: { min: 0, max: 0, default: 0 }
    },
    {
        id: 'eigen',
        name: 'Eigen indeling',
        description: "Uw roeden precies op de plek die u zelf in millimeters kiest.",
        adjustRows: false,
        adjustCols: false,
        rows: { min: 0, max: 0, default: 0 },
        cols: { min: 0, max: 0, default: 0 }
    }
]

export interface GlassOption {
    id: GlassId
    name: string
    description: string
    swatch: string
    privacy: 'geen' | 'hoog' | 'nvt'
}

export const GLASS_TYPES: GlassOption[] = [
    { id: 'helder', name: "Helder glas", description: "Gelaagd veiligheidsglas, maximaal licht en zicht.", swatch: '#dfe9e6', privacy: 'geen' },
    { id: 'mat', name: "Matglas", description: "Gezandstraald gelaagd glas. Laat licht door, maar geen inkijk.", swatch: '#e9ecec', privacy: 'hoog' },
    { id: 'op-aanvraag', name: "Glas op aanvraag", description: "Rookglas, ribbelglas of iets anders? Beschrijf uw wens, wij maken het voor u.", swatch: '#c9d4d4', privacy: 'nvt' }
]

export interface FinishOption {
    id: FinishId
    name: string
    code: string
    color: string
    metalness: number
    roughness: number
}

export const FINISHES: FinishOption[] = [
    { id: 'ral9005', name: "Zwart", code: 'RAL 9005', color: '#0e0e10', metalness: 0.45, roughness: 0.6 },
    { id: 'ral9016', name: "Wit", code: 'RAL 9016', color: '#f1f2ee', metalness: 0.12, roughness: 0.55 },
    { id: 'ral', name: "Kleur op aanvraag", code: 'RAL naar keuze', color: '#7a7a7a', metalness: 0.4, roughness: 0.55 }
]

export const RAL_COLORS: { code: string, name: string, hex: string }[] = [
    { code: 'RAL 1013', name: 'Parelwit', hex: '#e3d9c6' },
    { code: 'RAL 1015', name: 'Licht ivoor', hex: '#e6d2b5' },
    { code: 'RAL 2004', name: 'Zuiver oranje', hex: '#e75b12' },
    { code: 'RAL 3004', name: 'Purperrood', hex: '#75151e' },
    { code: 'RAL 3009', name: 'Oxiderood', hex: '#642424' },
    { code: 'RAL 5011', name: 'Staalblauw', hex: '#1a2b3c' },
    { code: 'RAL 6009', name: 'Dennengroen', hex: '#27352a' },
    { code: 'RAL 6012', name: 'Zwartgroen', hex: '#343e40' },
    { code: 'RAL 7015', name: 'Leigrijs', hex: '#51565c' },
    { code: 'RAL 7016', name: 'Antracietgrijs', hex: '#383e42' },
    { code: 'RAL 7021', name: 'Zwartgrijs', hex: '#2f3234' },
    { code: 'RAL 7022', name: 'Ombergrijs', hex: '#4b4d46' },
    { code: 'RAL 7024', name: 'Grafietgrijs', hex: '#474a50' },
    { code: 'RAL 7035', name: 'Lichtgrijs', hex: '#cbd0cc' },
    { code: 'RAL 7039', name: 'Kwartsgrijs', hex: '#6b695f' },
    { code: 'RAL 7040', name: 'Venstergrijs', hex: '#9da3a6' },
    { code: 'RAL 8014', name: 'Sepiabruin', hex: '#4a3526' },
    { code: 'RAL 8019', name: 'Grijsbruin', hex: '#3d3635' },
    { code: 'RAL 8022', name: 'Zwartbruin', hex: '#1a1718' },
    { code: 'RAL 9001', name: 'Crèmewit', hex: '#efebdc' },
    { code: 'RAL 9002', name: 'Grijswit', hex: '#ddded4' },
    { code: 'RAL 9003', name: 'Signaalwit', hex: '#f4f8f4' },
    { code: 'RAL 9004', name: 'Signaalzwart', hex: '#2e3032' },
    { code: 'RAL 9006', name: 'Blank aluminium', hex: '#a1a1a0' },
    { code: 'RAL 9007', name: 'Grijs aluminium', hex: '#878581' },
    { code: 'RAL 9010', name: 'Zuiver wit', hex: '#f7f9ef' },
    { code: 'RAL 9011', name: 'Grafietzwart', hex: '#1c1f21' },
    { code: 'RAL 9017', name: 'Verkeerszwart', hex: '#292c2f' }
]

export const normalizeRalCode = (value: string): string => {
    const trimmed = value.trim()
    const digits = /(\d{4})/.exec(trimmed)

    if (digits && /^(ral\s*)?\d{4}$/i.test(trimmed.replace(/\s+/g, ' '))) return `RAL ${digits[1]}`

    return trimmed
}

export const findRalColor = (code: string) => {
    const normalized = normalizeRalCode(code).toUpperCase()

    return RAL_COLORS.find(item => item.code.toUpperCase() === normalized) ?? null
}

export interface ProfileOption {
    id: ProfileId
    name: string
    description: string
    frame: number
    leaf: number
    bar: number
}

export const PROFILES: ProfileOption[] = [
    { id: 'slank', name: "Slank", description: "Ons dunste profiel, 20 mm zichtbaar.", frame: 25, leaf: 20, bar: 12 },
    { id: 'klassiek', name: "Klassiek", description: "Het herkenbare stalen profiel, 28 mm zichtbaar.", frame: 32, leaf: 28, bar: 16 },
    { id: 'robuust', name: "Robuust", description: "Industrieel en krachtig, 35 mm zichtbaar.", frame: 40, leaf: 35, bar: 20 }
]

export interface HandleOption {
    id: HandleId
    name: string
    description: string
}

export const HANDLES: HandleOption[] = [
    { id: 'kruk', name: "Deurkruk", description: "Klassieke kruk die u omlaag drukt, met dagschoot." },
    { id: 'klein', name: "Kleine vaste greep", description: "Compacte greep van 100 mm, strak en ingetogen." },
    { id: 'lang', name: "Greep over de volle lengte", description: "Lange vierkante greep over de hele hoogte van de deur." },
    { id: 'vaste-kruk', name: "Vaste deurkruk", description: "De vorm van een kruk, maar vast gemonteerd, met magneetsluiting." },
    { id: 'op-aanvraag', name: "Greep op aanvraag", description: "Een andere greep in gedachten? Beschrijf uw wens." }
]

export interface HandleFinishOption {
    id: HandleFinishId
    name: string
    color: string
    metalness: number
    roughness: number
}

export const HANDLE_FINISHES: HandleFinishOption[] = [
    { id: 'zwart', name: "Mat zwart", color: '#141414', metalness: 0.5, roughness: 0.5 },
    { id: 'rvs', name: "Geborsteld RVS", color: '#c3c6c9', metalness: 1, roughness: 0.32 },
    { id: 'messing', name: "Geborsteld messing", color: '#c8a35e', metalness: 1, roughness: 0.28 },
    { id: 'brons', name: "Brons", color: '#7f5e3c', metalness: 1, roughness: 0.36 }
]

export const SIDE_PANEL_OPTIONS: { id: SidePanelOption, name: string }[] = [
    { id: 'geen', name: "Geen" },
    { id: 'links', name: "Links" },
    { id: 'rechts', name: "Rechts" },
    { id: 'beide', name: "Beide zijden" }
]

export const SIDE_PANEL_RANGE: Range = { min: 300, max: 1000, default: 450 }

export const TRANSOM_RANGE: Range = { min: 300, max: 800, default: 450 }

export const NOTE_MAX_LENGTH = 160

export const findDoorType = (id: DoorTypeId): DoorTypeOption => DOOR_TYPES.find(item => item.id === id) ?? DOOR_TYPES[0]

export const findDivision = (id: DivisionId): DivisionOption => DIVISIONS.find(item => item.id === id) ?? DIVISIONS[0]

export const findGlass = (id: GlassId): GlassOption => GLASS_TYPES.find(item => item.id === id) ?? GLASS_TYPES[0]

export const findFinish = (id: FinishId): FinishOption => FINISHES.find(item => item.id === id) ?? FINISHES[0]

export const findProfile = (id: ProfileId): ProfileOption => PROFILES.find(item => item.id === id) ?? PROFILES[0]

export const findHandle = (id: HandleId): HandleOption => HANDLES.find(item => item.id === id) ?? HANDLES[0]

export const findHandleFinish = (id: HandleFinishId): HandleFinishOption => HANDLE_FINISHES.find(item => item.id === id) ?? HANDLE_FINISHES[0]

export const finishColor = (config: Pick<DoorConfig, 'finish' | 'ralCode'>): string => {
    if (config.finish === 'ral') return findRalColor(config.ralCode)?.hex ?? findFinish('ral').color

    return findFinish(config.finish).color
}

export const sidePanelCount = (config: Pick<DoorConfig, 'sidePanels' | 'doorType'>): number => {
    if (!findDoorType(config.doorType).allowsSidePanels) return 0
    if (config.sidePanels === 'beide') return 2
    if (config.sidePanels === 'geen') return 0
    return 1
}

export const hasTransom = (config: Pick<DoorConfig, 'transom' | 'doorType'>): boolean => {
    return findDoorType(config.doorType).allowsTransom && config.transom
}

export const DEFAULT_CONFIG: DoorConfig = {
    version: 2,
    doorType: 'draaideur',
    width: 930,
    height: 2315,
    division: 'strak',
    rows: 0,
    cols: 0,
    bars: [],
    glass: 'helder',
    glassNote: '',
    finish: 'ral9005',
    ralCode: '',
    profile: 'slank',
    handle: 'lang',
    handleNote: '',
    handleFinish: 'zwart',
    sidePanels: 'geen',
    sidePanelWidth: 450,
    transom: false,
    transomHeight: 450,
    bottomPanel: false,
    hinge: 'links',
    lock: false,
    softClose: false
}
